import React, { useContext, useEffect, useMemo, Suspense, useState } from 'react';
import { Spin } from 'antd';
import { ApiOutlined, InboxOutlined } from '@ant-design/icons';
import { useAndStore } from '../../base/store/useAndStore';
import { getDataSetStoreKey } from '../../base/storeKeyUtils';
import { parseArrayNotation } from '../../base/store/storePathUtils';
import { getCoreConfig } from '../../config';
import { DataSetInheritContext } from '../../base/DataSetInheritContext';

export interface AndDynamicContainerProps {
  /** 要渲染的组件名称 */
  componentName: string;
  /** 静态 props */
  componentProps?: Record<string, any>;
  /** 从 Store 获取 props 的 key */
  storePropsKey?: string;
  /** 会话 ID */
  sessionId?: string;
  /** 容器标题 (仅设计模式显示) */
  title?: string;
  /** 是否开发/设计模式 */
  develop?: boolean;
  /** 容器样式 */
  style?: React.CSSProperties;
  /** ID */
  id?: string;
  /** 组件解析器 - 由运行时注入 */
  componentResolver?: (name: string) => React.ComponentType<any> | null;
  /** 占位符文本 - 当组件未配置或未找到时显示 */
  placeholder?: string;
  /** 仅设计器：占位内容（图标/标题/storeKey）的 flex 方向 */
  placeholderFlexDirection?: 'column' | 'row';
  /**
   * 迭代器行下标（由父级传入，如 AndIterator.map 的 index）。
   * - 会并入传给动态根组件的 props，供子树内拼 dataSetField（如 `items[${index}].field`）。
   * - 当 storePropsKey 的首段无字面量 [n] 时，与 storePropsKey 组合为按行读取 store（等价于 baseKey[index]）。
   */
  index?: number;
}

// 占位符组件 - 统一样式，专业简洁的灰色风格
const PlaceholderBox: React.FC<{
  id?: string;
  icon?: React.ReactNode;
  title?: string;
  description?: string;
  style?: React.CSSProperties;
}> = ({ id, icon, title, description, style }) => (
  <div
    id={id}
    style={{
      width: '100%',
      height: '100%',
      minHeight: 80,
      flex: '1 1 auto',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      border: '1px dashed #d9d9d9',
      borderRadius: 4,
      background: '#fafafa',
      color: '#8c8c8c',
      padding: 16,
      boxSizing: 'border-box',
      ...style,
    }}
  >
    {icon || <InboxOutlined style={{ fontSize: 28, marginBottom: 8, color: '#bfbfbf' }} />}
    {title && <div style={{ fontWeight: 500, fontSize: 13, marginBottom: 4 }}>{title}</div>}
    {description && <div style={{ fontSize: 12, opacity: 0.7 }}>{description}</div>}
  </div>
);

/** main 壳层 DynamicContainer 已访问且需 keep-alive 的 route key */
const shellKeepAliveVisited = new Map<string, Set<string>>();

function isMainShellDynamicRoute(sessionId?: string, storePropsKey?: string): boolean {
  return sessionId === 'main' && storePropsKey === 'dynamicRoute';
}

/**
 * AndDynamicContainer - 动态容器组件
 * 
 * 在设计器中显示为占位符卡片，运行时动态加载指定组件。
 * 
 * 使用方式：
 * 1. 设计器：拖入画布，配置 componentName 和 storePropsKey
 * 2. 运行时：通过 componentResolver 注入组件解析能力
 * 3. 迭代器内使用时传入 index，动态根组件可收到同名的 props.index
 */
export const AndDynamicContainer: React.FC<AndDynamicContainerProps> = ({
  componentName,
  componentProps = {},
  storePropsKey,
  sessionId,
  title,
  develop = false,
  style,
  id,
  componentResolver,
  placeholder,
  placeholderFlexDirection = 'column',
  index,
}) => {
  const inheritCtx = useContext(DataSetInheritContext);

  // dataSet key 统一用 getDataSetStoreKey(sessionId, baseKey)，避免命名冲突。
  // 下标来源：① storePropsKey 首段字面量 dynamicContainer[0]；② 否则若传入 index，则用 index（迭代器行下标）。
  const { storeBaseKey, arrayIndex } = useMemo(() => {
    if (!storePropsKey) return { storeBaseKey: undefined, arrayIndex: null as number | null };
    const rawKey = storePropsKey.split('.')[0];
    const { baseName, index: idxFromKey } = parseArrayNotation(rawKey);
    let arrayIndex: number | null = idxFromKey;
    if (arrayIndex === null && index !== undefined && index !== null) {
      const n = Number(index);
      arrayIndex = Number.isFinite(n) ? n : null;
    }
    // 二层动态容器等场景下，若未显式传 index，则回退使用继承上下文中的索引。
    if (arrayIndex === null) {
      const ctxIndex = Number(inheritCtx?.index);
      if (Number.isFinite(ctxIndex)) {
        arrayIndex = ctxIndex;
      } else {
        const ctxParentIndex = Number(inheritCtx?.parentIndex);
        arrayIndex = Number.isFinite(ctxParentIndex) ? ctxParentIndex : null;
      }
    }
    return {
      storeBaseKey: getDataSetStoreKey(sessionId, baseName),
      arrayIndex,
    };
  }, [storePropsKey, sessionId, index, inheritCtx]);

  // 与 setDataSet/getDataSet 带 [n] 的约定一致：按行数据在 dataSets[key].value[index]
  const dataSetSlice = useAndStore((state) => {
    if (!storeBaseKey) return null;
    const ds = state.dataSets[storeBaseKey];
    if (arrayIndex !== null) {
      const arr = ds?.value;
      if (!Array.isArray(arr)) {
        // singleton 容器（由 setDynamicContainer/updateItem 写入 { componentName, props }），
        // 当 arrayIndex 因 inheritCtx fallback 非空时，仍应读取整个 singleton 而不是返回 null。
        if (ds != null && typeof (ds as any).componentName === 'string') return ds;
        return null;
      }
      const parentIndex = Number(
        inheritCtx?.parentIndex !== undefined
          ? inheritCtx.parentIndex
          : inheritCtx?.index
      );
      if (Number.isFinite(parentIndex)) {
        const parentSlice = arr[parentIndex];
        if (Array.isArray(parentSlice)) {
          return parentSlice[arrayIndex] ?? null;
        }
      }
      return arr[arrayIndex] ?? null;
    }
    return ds ?? null;
  });

  // 从 store 获取配置（包括 componentName 和 props）
  const storeData = useMemo(() => {
    if (!storePropsKey || !dataSetSlice) return { componentName: '', props: {} };
    const parts = storePropsKey.split('.');
    const field = parts[1];
    const data = field ? (dataSetSlice[field] || {}) : dataSetSlice;
    
    // 支持两种格式：
    // 1. { componentName: 'xxx', ...props } - componentName 和 props 在同一对象
    // 2. { componentName: 'xxx', props: {...} } - props 嵌套在 props 字段
    if (typeof data === 'object' && data !== null) {
      const { componentName: storeCN, props: storeP, ...restProps } = data as any;
      return {
        componentName: storeCN || '',
        props: storeP || restProps || {}
      };
    }
    
    return { componentName: '', props: {} };
  }, [storePropsKey, sessionId, dataSetSlice]);

  const inheritDataSet =
    (storeData.props as any)?.inheritDataSet !== undefined
      ? String((storeData.props as any)?.inheritDataSet)
      : undefined;
  const inheritDataSetFieldRoot =
    (storeData.props as any)?.inheritDataSetFieldRoot !== undefined
      ? String((storeData.props as any)?.inheritDataSetFieldRoot)
      : undefined;

  // 优先使用 store 中的 componentName，否则使用 prop
  const effectiveComponentName = storeData.componentName || componentName;

  // 合并 props：store props + 迭代器 index + 静态 props（后者可覆盖前几项）
  const mergedProps = useMemo(() => ({
    // inheritDataSet / inheritId 只用于给后代提供 DataSetInheritContext，不应透传给具体组件。
    ...(storeData.props ? (() => {
      const {
        inheritDataSet: _ignored,
        inheritDataSetFieldRoot: _ignoredFieldRoot,
        inheritId: _ignoredInheritId,
        ...rest
      } = storeData.props as any;
      return rest;
    })() : {}),
    ...(index !== undefined ? { index } : {}),
    ...componentProps,
    sessionId,
  }), [storeData.props, componentProps, sessionId, index]);

  const inheritContextValue = useMemo(() => {
    if (!inheritDataSet) return null;

    // context index：优先读 storeData.props.index（由 setDynamicContainer 运行时写入），
    // 其次用 arrayIndex（来自 JSX prop 或 storePropsKey 字面量下标）。
    // 当 fieldRoot 是绝对路径（以 '[' 开头）时，item 索引已内嵌在路径中，
    // 不再注入 index 到 context，防止 useResolvedDataSet 二次应用索引导致路径错误。
    const isAbsoluteFieldRoot =
      !!inheritDataSetFieldRoot && inheritDataSetFieldRoot.trim().startsWith("[");
    const propsIndex = (storeData.props as any)?.index;
    const effectiveIndex = isAbsoluteFieldRoot
      ? undefined
      : typeof propsIndex === 'number' && Number.isFinite(propsIndex)
        ? propsIndex
        : arrayIndex !== null
          ? arrayIndex
          : undefined;

    // context parentIndex：优先读 storeData.props.parentIndex（运行时显式传），
    // 若与父级同一 dataset 则不从父级继承（避免错误晋升同维度 index）；
    // 否则从父级 context 晋升 index → parentIndex。
    const propsParentIndex = (storeData.props as any)?.parentIndex;
    // 与父级同 dataset（如表格单元格 Masonry 内再套 DC）时仍晋升外层 index，避免 propsStore 与 deriveScopedComponentId 丢维。
    let effectiveParentIndex: number | undefined;
    if (typeof propsParentIndex === 'number' && Number.isFinite(propsParentIndex)) {
      effectiveParentIndex = propsParentIndex;
    } else if (inheritCtx) {
      const contextParent = inheritCtx?.parentIndex ?? inheritCtx?.index;
      effectiveParentIndex = contextParent !== undefined ? Number(contextParent) : undefined;
      if (effectiveParentIndex !== undefined && !Number.isFinite(effectiveParentIndex)) {
        effectiveParentIndex = undefined;
      }
    }

    const rawProps = storeData.props as Record<string, unknown> | undefined;
    let inheritContextId: string | number | undefined;
    if (rawProps?.inheritId !== undefined && rawProps?.inheritId !== null) {
      inheritContextId = rawProps.inheritId as string | number;
    } else if (rawProps?.id !== undefined && rawProps?.id !== null) {
      inheritContextId = rawProps.id as string | number;
    } else {
      inheritContextId = undefined;
    }

    return {
      dataSet: inheritDataSet,
      ...(inheritContextId !== undefined ? { id: inheritContextId } : {}),
      ...(effectiveIndex !== undefined ? { index: effectiveIndex } : {}),
      ...(effectiveParentIndex !== undefined ? { parentIndex: effectiveParentIndex } : {}),
      ...(inheritCtx ? { parentContext: inheritCtx } : {}),
      ...(inheritDataSetFieldRoot ? { dataSetFieldRoot: inheritDataSetFieldRoot } : {}),
      sessionId,
    };
  }, [inheritDataSet, arrayIndex, storeData.props, inheritCtx, inheritDataSetFieldRoot, sessionId]);

  // 默认占位符文本
  const defaultPlaceholder = placeholder || '请配置组件';

  const keepAliveNames = getCoreConfig().keepAliveComponentNames;
  const shellKeepAliveKey =
    sessionId && storePropsKey ? `${sessionId}:${storePropsKey}` : '';
  const useShellKeepAlive = Boolean(
    isMainShellDynamicRoute(sessionId, storePropsKey) &&
      keepAliveNames &&
      keepAliveNames.length > 0,
  );

  const [keepAliveVersion, bumpKeepAlive] = useState(0);
  useEffect(() => {
    if (!useShellKeepAlive || !shellKeepAliveKey || !effectiveComponentName) return;
    if (!keepAliveNames!.includes(effectiveComponentName)) return;
    let set = shellKeepAliveVisited.get(shellKeepAliveKey);
    if (!set) {
      set = new Set();
      shellKeepAliveVisited.set(shellKeepAliveKey, set);
    }
    if (!set.has(effectiveComponentName)) {
      set.add(effectiveComponentName);
      bumpKeepAlive((n) => n + 1);
    }
  }, [useShellKeepAlive, shellKeepAliveKey, effectiveComponentName, keepAliveNames]);

  const keepAliveRouteNames = useMemo(() => {
    if (!useShellKeepAlive || !shellKeepAliveKey) return [];
    const visited = shellKeepAliveVisited.get(shellKeepAliveKey) ?? new Set<string>();
    if (effectiveComponentName && keepAliveNames!.includes(effectiveComponentName)) {
      visited.add(effectiveComponentName);
    }
    return [...visited].filter((n) => keepAliveNames!.includes(n));
  }, [
    useShellKeepAlive,
    shellKeepAliveKey,
    effectiveComponentName,
    keepAliveNames,
    keepAliveVersion,
  ]);

  const suspenseFallback = (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        height: '100%',
      }}
    >
      <Spin />
    </div>
  );

  const renderResolvedComponent = (
    Resolved: React.ComponentType<any>,
    routeKey: string,
    routeProps: Record<string, any>,
  ) => {
    const inner = (
      <Suspense fallback={suspenseFallback}>
        <Resolved {...routeProps} />
      </Suspense>
    );
    if (inheritContextValue) {
      return (
        <DataSetInheritContext.Provider value={inheritContextValue}>
          {inner}
        </DataSetInheritContext.Provider>
      );
    }
    return inner;
  };

  // 设计模式：显示占位符（图标、标题、未配置/storeKey），布局方向由 placeholderFlexDirection 控制
  if (develop) {
    const isRow = placeholderFlexDirection === 'row';
    return (
      <div
        id={id}
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexGrow: 1,
          flexDirection: placeholderFlexDirection,
          alignItems: 'center',
          justifyContent: 'center',
          gap: isRow ? 8 : 0,
          border: '2px dashed #d9d9d9',
          borderRadius: 4,
          color: '#8c8c8c',
          background: 'transparent',
          boxSizing: 'border-box',
        }}
      >
        <ApiOutlined style={{ fontSize: 20, flexShrink: 0, color: '#bfbfbf', ...(isRow ? {} : { marginBottom: 4 }) }} />
        <div style={{ fontWeight: 500, fontSize: 12 }}>
          {title || '动态容器'}
        </div>
        <div style={{ fontSize: 11, opacity: 0.7 }}>
          {effectiveComponentName ? `${effectiveComponentName}` : '未配置'}
        </div>
        {storePropsKey && (
          <div style={{ fontSize: 10, opacity: 0.5, marginTop: isRow ? 0 : 2 }}>
            {storePropsKey}
          </div>
        )}
      </div>
    );
  }

  // 运行模式：动态加载组件
  // 优先使用 prop 传入的 resolver，否则使用全局配置的 resolver
  const globalConfig = getCoreConfig();
  const effectiveResolver = componentResolver || globalConfig.componentResolver;
  
  // 无 resolver 时显示占位符（不再显示警告色）
  if (!effectiveResolver) {
    return (
      <PlaceholderBox
        id={id}
        title={defaultPlaceholder}
        description="动态容器未配置组件解析器"
        style={style}
      />
    );
  }

  const Component = effectiveComponentName ? effectiveResolver(effectiveComponentName) : null;
  
  // 组件未找到时显示占位符（不再显示警告色）
  if (!Component) {
    return (
      <PlaceholderBox
        id={id}
        title={defaultPlaceholder}
        description={effectiveComponentName ? `组件 "${effectiveComponentName}" 未找到` : undefined}
        style={style}
      />
    );
  }

  if (useShellKeepAlive && keepAliveRouteNames.length > 0) {
    const activeInKeepAlive =
      effectiveComponentName && keepAliveNames!.includes(effectiveComponentName);
    return (
      <div
        id={id}
        style={{
          width: '100%',
          height: '100%',
          flex: '1 1 auto',
          position: 'relative',
          minHeight: 0,
          ...style,
        }}
      >
        {keepAliveRouteNames.map((routeKey) => {
          const Resolved = effectiveResolver(routeKey);
          if (!Resolved) return null;
          const isActive = routeKey === effectiveComponentName;
          return (
            <div
              key={routeKey}
              style={{
                display: isActive ? 'flex' : 'none',
                flexDirection: 'column',
                width: '100%',
                height: '100%',
                flex: '1 1 auto',
                minHeight: 0,
              }}
              aria-hidden={!isActive}
            >
              {renderResolvedComponent(Resolved, routeKey, mergedProps)}
            </div>
          );
        })}
        {!activeInKeepAlive && effectiveComponentName && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              height: '100%',
              flex: '1 1 auto',
              minHeight: 0,
            }}
          >
            {renderResolvedComponent(Component, effectiveComponentName, mergedProps)}
          </div>
        )}
      </div>
    );
  }

  return (
    <Suspense fallback={suspenseFallback}>
      <div id={id} style={{ width: '100%', height: '100%', flex: '1 1 auto', ...style }}>
        {inheritContextValue ? (
          <DataSetInheritContext.Provider
            value={inheritContextValue}
          >
            <Component {...mergedProps} />
          </DataSetInheritContext.Provider>
        ) : (
          <Component {...mergedProps} />
        )}
      </div>
    </Suspense>
  );
};

export default AndDynamicContainer;
